import React, { useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Button,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import classnames from "classnames";
import HeadersRole from "../../../Components/Headers/HeadersRole";
import AdminCommissionStructuresModal from "../../../Components/modals/AdminCommissionModal/AdminCommissionStructuresModal";
import UserCommissionManagementModal from "../../../Components/modals/AdminCommissionModal/UserCommissionManagementModal";
import { useCurrentUser } from "../../../slices/auth/hooks";
import commissionIcon from "../../../assets/images/iconsRoles/nationalIcon.svg"; // You can add a specific commission icon

const CommissionManagement = () => {
  const currentUser = useCurrentUser();
  const [activeTab, setActiveTab] = useState("1");
  const [openCommissionStructuresModal, setOpenCommissionStructuresModal] = useState(false);
  const [openUserCommissionModal, setOpenUserCommissionModal] = useState(false);

  const toggleTab = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
    }
  };

  const handleOpenStructuresModal = () => {
    setOpenCommissionStructuresModal(true);
  };

  const handleOpenUserCommissionModal = () => {
    setOpenUserCommissionModal(true);
  };

  const commissionLabel = (
    <span className="text-muted">Manage commission structures and user assignments</span>
  );

  return (
    <div className="page-content">
      <HeadersRole
        role={"Commission Management"}
        icon={commissionIcon}
        svg={true}
        text={commissionLabel}
      />

      {/* Modals */}
      <AdminCommissionStructuresModal
        isOpen={openCommissionStructuresModal}
        setIsOpen={setOpenCommissionStructuresModal}
        currentUser={currentUser}
      />
      <UserCommissionManagementModal
        isOpen={openUserCommissionModal}
        setIsOpen={setOpenUserCommissionModal}
        currentUser={currentUser}
      />

      <Container fluid>
        <Row>
          <Col>
            <Card>
              <CardBody>
                <Nav tabs className="nav-tabs-custom nav-justified mb-3">
                  <NavItem>
                    <NavLink
                      style={{ cursor: "pointer" }}
                      className={classnames({
                        active: activeTab === "1",
                      })}
                      onClick={() => toggleTab("1")}
                    >
                      <i className="ri-organization-chart me-1"></i>
                      Commission Structures
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      style={{ cursor: "pointer" }}
                      className={classnames({
                        active: activeTab === "2",
                      })}
                      onClick={() => toggleTab("2")}
                    >
                      <i className="ri-user-settings-line me-1"></i>
                      User Commission Management
                    </NavLink>
                  </NavItem>
                </Nav>

                <TabContent activeTab={activeTab}>
                  <TabPane tabId="1">
                    <div className="text-center">
                      <div className="mb-4">
                        <i className="ri-organization-chart display-4 text-primary"></i>
                      </div>
                      <h4>Commission Structures Management</h4>
                      <p className="text-muted mb-4">
                        Create, edit, and manage commission structure templates that can be assigned to users.
                        These structures define how commission amounts are split between National, GA, and Broker roles.
                      </p>
                      <div className="mb-3">
                        <h6 className="text-start">Features:</h6>
                        <ul className="text-start text-muted">
                          <li>Create custom commission structures with percentage splits</li>
                          <li>Set default structures for new users</li>
                          <li>Edit existing structures and their assignments</li>
                          <li>Delete unused structures with safety checks</li>
                          <li>Validate that percentages sum to total commission amount</li>
                        </ul>
                      </div>
                      <Button
                        color="primary"
                        size="lg"
                        onClick={handleOpenStructuresModal}
                      >
                        <i className="ri-organization-chart me-1"></i>
                        Manage Commission Structures
                      </Button>
                    </div>
                  </TabPane>

                  <TabPane tabId="2">
                    <div className="text-center">
                      <div className="mb-4">
                        <i className="ri-user-settings-line display-4 text-info"></i>
                      </div>
                      <h4>User Commission Management</h4>
                      <p className="text-muted mb-4">
                        Search for and manage individual user commission settings.
                        Set custom commission amounts and assign specific commission structures to users.
                      </p>
                      <div className="mb-3">
                        <h6 className="text-start">Features:</h6>
                        <ul className="text-start text-muted">
                          <li>Search for users by name or email</li>
                          <li>View current commission settings and breakdown</li>
                          <li>Set custom commission total amounts (default: $10.00)</li>
                          <li>Assign custom commission structures to users</li>
                          <li>Real-time preview of commission changes</li>
                        </ul>
                      </div>
                      <Button
                        color="info"
                        size="lg"
                        onClick={handleOpenUserCommissionModal}
                      >
                        <i className="ri-user-settings-line me-1"></i>
                        Manage User Commissions
                      </Button>
                    </div>
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row className="mt-4">
          <Col md={6}>
            <Card className="bg-soft-primary border-primary">
              <CardBody>
                <div className="d-flex align-items-center">
                  <div className="flex-shrink-0">
                    <i className="ri-information-line display-6 text-primary"></i>
                  </div>
                  <div className="flex-grow-1 ms-3">
                    <h5 className="text-primary">Commission Structure Rules</h5>
                    <p className="text-muted mb-0">
                      • Total percentages must equal 1000 cents ($10.00)<br/>
                      • National, GA, and Broker amounts must be ≥ 0<br/>
                      • Each structure requires a National user assignment<br/>
                      • Default structures are used for new users
                    </p>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col md={6}>
            <Card className="bg-soft-info border-info">
              <CardBody>
                <div className="d-flex align-items-center">
                  <div className="flex-shrink-0">
                    <i className="ri-shield-check-line display-6 text-info"></i>
                  </div>
                  <div className="flex-grow-1 ms-3">
                    <h5 className="text-info">Safety Features</h5>
                    <p className="text-muted mb-0">
                      • Deletion requires confirmation and understanding<br/>
                      • Users revert to default when structure is deleted<br/>
                      • All changes are validated before saving<br/>
                      • Real-time error feedback and notifications
                    </p>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default CommissionManagement;